<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { ValidData as AttachmentsFormValidData } from '@/components/forms/DetailedJobAttachmentsForm.vue';
import type { DataItem as QueryDataItem } from '@/components/tables/DetailedJobQueriesTable.vue';
import type { DataItem as TicketDataItem } from '@/components/tables/DetailedJobTicketsTable.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import DetailedJobAttachmentsDescList from '@/components/descLists/DetailedJobAttachmentsDescList.vue';
import DetailedJobAttachmentsForm from '@/components/forms/DetailedJobAttachmentsForm.vue';
import JobEditAccordionSection from '@/components/sections/JobEditAccordionSection.vue';
import DetailedJobQueriesTable from '@/components/tables/DetailedJobQueriesTable.vue';
import DetailedJobTicketsTable from '@/components/tables/DetailedJobTicketsTable.vue';

export interface Props {
  jobId?: number | null;
  jobDate?: string | null;
  attachments?: ReadonlyDeep<openapi.JobDetailedOpenapi['attachments']>;
  queriesData?: ReadonlyDeep<QueryDataItem[]>;
  ticketsData?: ReadonlyDeep<TicketDataItem[]>;
  isLoadingMessages?: boolean;
  isLoadingTickets?: boolean;
  isLoadingAttachments?: boolean;
  isStaff?: boolean;
  refreshDetailedJob?: () => Promise<void>;
}

const props = withDefaults(defineProps<Props>(), {
  jobId: null,
  jobDate: null,
  attachments: () => [],
  queriesData: () => [],
  ticketsData: () => [],
  isLoadingMessages: false,
  isLoadingTickets: false,
  isLoadingAttachments: false,
  isStaff: false,
  refreshDetailedJob: undefined,
});

const emit = defineEmits<{
  newMessage: [];
  newTicket: [];
  submitAttachments: [formData: AttachmentsFormValidData, cb: () => void];
}>();

const MESSAGES_PREVIEW_LIMIT = 3;
const showAllMessages = ref(false);

const visibleQueriesData = computed(() => {
  if (
    showAllMessages.value ||
    props.queriesData.length <= MESSAGES_PREVIEW_LIMIT
  ) {
    return props.queriesData;
  }
  return props.queriesData.slice(0, MESSAGES_PREVIEW_LIMIT);
});

const hasMoreMessages = computed(
  () =>
    props.queriesData.length > MESSAGES_PREVIEW_LIMIT && !showAllMessages.value,
);

const ticketsCount = computed(() => props.ticketsData.length);

function handleSeeAllMessages(): void {
  showAllMessages.value = true;
}
</script>

<template>
  <JobEditAccordionSection
    :class="$style.jobEditOtherSection"
    title="OTHER"
    data-testid="jobEditOtherSection"
  >
    <template v-if="ticketsCount" #collapsedContent>
      <span
        >{{ ticketsCount }}
        {{ ticketsCount === 1 ? 'Ticket' : 'Tickets' }}</span
      >
    </template>
    <div :class="$style.topRow">
      <div :class="$style.messagesColumn">
        <div :class="$style.columnHeader">
          <v-icon :class="$style.icon" icon="mdi-message-text" />
          <span :class="$style.columnTitle">MESSAGES</span>
          <KBtn
            variant="text"
            prepend-icon="mdi-plus"
            size="sm"
            :class="$style.actionBtn"
            @click="emit('newMessage')"
          >
            NEW MESSAGE
          </KBtn>
        </div>
        <DetailedJobQueriesTable
          :data="visibleQueriesData"
          :loading="props.isLoadingMessages"
          :is-staff="props.isStaff"
        />
        <div
          v-if="hasMoreMessages"
          :class="$style.seeAllLink"
          @click="handleSeeAllMessages"
        >
          SEE ALL MESSAGES
        </div>
      </div>

      <div :class="$style.attachmentsColumn">
        <div :class="$style.columnHeader">
          <v-icon :class="$style.icon" icon="mdi-paperclip" />
          <span :class="$style.columnTitle">ATTACHMENTS</span>
        </div>
        <DetailedJobAttachmentsDescList
          v-if="props.attachments?.length"
          :attachments="props.attachments"
          :refresh-detailed-job="props.refreshDetailedJob"
          :class="$style.attachmentsList"
        />
        <DetailedJobAttachmentsForm
          :loading="props.isLoadingAttachments"
          drop-zone-variant="dropzone"
          @submit="(formData, cb) => emit('submitAttachments', formData, cb)"
        />
      </div>
    </div>

    <div :class="$style.ticketsSection">
      <div :class="$style.columnHeader">
        <v-icon :class="$style.icon" icon="mdi-file-document-outline" />
        <span :class="$style.columnTitle">TICKETS</span>
        <KBtn
          variant="text"
          prepend-icon="mdi-plus"
          size="sm"
          :class="$style.actionBtn"
          @click="emit('newTicket')"
        >
          NEW TICKET
        </KBtn>
      </div>
      <DetailedJobTicketsTable
        :data="props.ticketsData"
        :loading="props.isLoadingTickets"
      />
    </div>
  </JobEditAccordionSection>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.jobEditOtherSection {
  max-width: 100%;
}

.topRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
}

.messagesColumn,
.attachmentsColumn {
  background: colors.$color-white;
  border: 1px solid colors.$color-gray-200;
  border-radius: 8px;
  padding: 16px;
}

.ticketsSection {
  background: colors.$color-white;
  border: 1px solid colors.$color-gray-200;
  border-radius: 8px;
  padding: 16px;
}

.columnHeader {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.icon {
  font-size: 18px;
  color: colors.$color-brand-500;
  flex-shrink: 0;
}

.columnTitle {
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.5px;
  color: colors.$color-text-primary;
  text-transform: uppercase;
}

.actionBtn {
  margin-left: auto;
  text-transform: uppercase;
  font-size: 11px;
  font-weight: 500;
  flex-shrink: 0;
  color: colors.$color-brand-500;
}

.seeAllLink {
  margin-top: 12px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: colors.$color-brand-500;
  text-transform: uppercase;
  cursor: pointer;
  padding: 8px;

  &:hover {
    text-decoration: underline;
  }
}

.attachmentsList {
  margin-bottom: 16px;
}
</style>
